import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";

// Cache em memória: a conversa é recarregada a cada poucos segundos e cada
// arquivo só precisa ser baixado uma vez por sessão.
const urlCache = new Map();

async function fetchMediaUrl(fileId) {
  if (urlCache.has(fileId)) return urlCache.get(fileId);
  const r = await api.get(`/chat-media/${fileId}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  urlCache.set(fileId, url);
  return url;
}

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const TRANSCRIPT_NOTES = {
  empty: "(nenhuma fala reconhecida)",
  skipped: "(áudio muito longo, sem transcrição)",
  failed: "(transcrição indisponível)",
};

export function MediaView({ media, transcript, transcriptStatus, onLoad }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const stored = media?.status === "stored" && !!media?.file_id;
  // Imagem, figurinha e áudio carregam sozinhos; vídeo e documento só ao clicar.
  const auto = stored && ["image", "sticker", "audio"].includes(media.kind);

  useEffect(() => {
    let alive = true;
    if (auto) {
      fetchMediaUrl(media.file_id)
        .then((u) => alive && setUrl(u))
        .catch(() => alive && setFailed(true));
    }
    return () => { alive = false; };
  }, [auto, media?.file_id]);

  if (!media) return null;
  if (media.status === "expired")
    return <div className="text-xs italic text-slate-400">📎 Arquivo removido do servidor (mais de 30 dias).</div>;
  if (media.status === "too_large")
    return <div className="text-xs italic text-slate-400">📎 Arquivo acima de 20 MB, não foi armazenado.</div>;
  if (!stored || failed)
    return <div className="text-xs italic text-slate-400">📎 Não foi possível carregar o arquivo.</div>;

  const load = async () => {
    setBusy(true);
    try { setUrl(await fetchMediaUrl(media.file_id)); } catch { setFailed(true); } finally { setBusy(false); }
  };

  if (media.kind === "image" || media.kind === "sticker") {
    return url ? (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt="imagem da conversa" onLoad={onLoad} data-testid="chat-media-image"
             className={`rounded-md ${media.kind === "sticker" ? "h-28" : "max-h-64 max-w-full"} object-contain`} />
      </a>
    ) : <div className="text-xs text-slate-400">Carregando imagem…</div>;
  }

  if (media.kind === "audio") {
    return (
      <div className="space-y-1.5" data-testid="chat-media-audio">
        {url ? <audio controls src={url} className="h-9 max-w-full" onLoadedMetadata={onLoad} />
             : <div className="text-xs text-slate-400">Carregando áudio…</div>}
        <div className="text-xs rounded bg-black/20 px-2 py-1.5 whitespace-pre-wrap">
          <span className="text-slate-400">📝 Transcrição: </span>
          {transcript ? transcript
            : TRANSCRIPT_NOTES[transcriptStatus] || <span className="italic text-slate-400">transcrevendo…</span>}
        </div>
      </div>
    );
  }

  if (media.kind === "video") {
    return url ? (
      <video controls src={url} className="max-h-64 max-w-full rounded-md" onLoadedMetadata={onLoad} />
    ) : (
      <button type="button" onClick={load} disabled={busy}
              className="text-xs underline text-emerald-300 hover:text-emerald-200">
        {busy ? "Carregando vídeo…" : `🎥 Carregar vídeo ${fmtSize(media.size)}`}
      </button>
    );
  }

  // documento
  const name = media.filename || "documento";
  return url ? (
    <a href={url} download={name} target="_blank" rel="noreferrer"
       className="text-xs underline text-emerald-300 hover:text-emerald-200">📄 Abrir {name}</a>
  ) : (
    <button type="button" onClick={load} disabled={busy}
            className="text-xs underline text-emerald-300 hover:text-emerald-200">
      {busy ? "Carregando…" : `📄 ${name} ${fmtSize(media.size) ? `(${fmtSize(media.size)})` : ""}`}
    </button>
  );
}
